import { materialTokenAusPfad } from './materialKarte';
import type { MaterialKarte, MaterialMeldungKurz, MeldenAntwort } from './materialTypes';

/**
 * Reine Logik der öffentlichen Meldeseite `<origin>/m/<token>`
 * (docs/materialverwaltung-plan.md, Client 2./3.).
 *
 * Kein React, kein Supabase: Route, Zustandsentscheidungen, alle Texte,
 * relative Zeiten und Namensbereinigung. Die Komponenten bilden nur noch
 * `ansichtFuer(…)` ab (kein jsdom im Test, Muster freimeldungLogic.ts).
 *
 * Die Seite wird mit der Handykamera geöffnet, meist ohne Anmeldung. Sie
 * verrät nie, warum eine Karte ungültig ist: unbekannter Token, deaktivierter
 * Artikel und fehlende Migration bekommen denselben Satz.
 */

// ─── Route ───────────────────────────────────────────────────────────────────

/** Die Meldeseite: `token` null heißt „Pfad /m/…, aber kein gültiger Token“ → Karte ungültig. */
export interface MaterialRoute {
    token: string | null;
}

/**
 * Pfad → Meldeseite. null = keine Material-Adresse (dann entscheidet index.tsx
 * weiter über den Hash). Groß-/Kleinschreibung zählt nicht: Hex ist Hex, und
 * ein QR im Alphanumerik-Modus (nur Großbuchstaben, kleinerer Code) käme sonst
 * als `/M/0123…ABCD` an.
 */
export const materialRouteAusPfad = (pathname: string | null | undefined): MaterialRoute | null => {
    const pfad = (pathname ?? '').toLowerCase();
    if (!pfad.startsWith('/m/')) return null;
    return { token: materialTokenAusPfad(pfad) };
};

/**
 * Schlüssel, an dem index.tsx einen Seitenwechsel erkennt (dann Neuladen). Auf
 * der Meldeseite wählt der PFAD die Seite, ein Hash-Wechsel ändert daran
 * nichts — der Schlüssel bleibt stehen. Sonst gilt der Schlüssel der
 * Hash-Routen (`routenSchluessel` aus sifa/public/routenLogic).
 */
export const seitenSchluessel = (pathname: string | null | undefined, hashSchluessel: string): string =>
    materialRouteAusPfad(pathname) ? `material:${(pathname ?? '').toLowerCase().replace(/\/+$/, '')}` : hashSchluessel;

// ─── Zeit ────────────────────────────────────────────────────────────────────

const BERLIN = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

interface BerlinTeile {
    wochentag: string;
    jahr: number;
    monat: string;
    tag: string;
    stunde: string;
    minute: string;
}

const zeitAus = (wert: string | number | null | undefined): number | null => {
    if (wert === null || wert === undefined || wert === '') return null;
    const ms = typeof wert === 'number' ? wert : Date.parse(wert);
    return Number.isFinite(ms) ? ms : null;
};

const berlinTeile = (ms: number): BerlinTeile => {
    const teile = BERLIN.formatToParts(ms);
    const wert = (typ: Intl.DateTimeFormatPartTypes): string => teile.find((t) => t.type === typ)?.value ?? '';
    return {
        // „Mo.“ → „Mo“, wie auf der Karte gewünscht („Mo 26.10., 09:14“).
        wochentag: wert('weekday').replace(/\.$/, ''),
        jahr: Number(wert('year')),
        monat: wert('month'),
        tag: wert('day'),
        stunde: wert('hour'),
        minute: wert('minute'),
    };
};

/** Laufende Nummer des Berliner Kalendertags — Grundlage für „heute“/„vor X Tagen“. */
const berlinerTagNr = (ms: number): number => {
    const t = berlinTeile(ms);
    return Math.round(Date.UTC(t.jahr, Number(t.monat) - 1, Number(t.tag)) / 86_400_000);
};

/**
 * Abstand in Berliner Kalendertagen: „heute“, „vor 1 Tag“, „vor X Tagen“.
 * Kalendertage statt 24-h-Blöcken — eine Meldung von gestern 23:50 ist um
 * 00:10 „vor 1 Tag“, nicht „heute“. Ein Zeitpunkt in der Zukunft (Uhr des
 * Handys geht nach) gilt als „heute“. Unlesbar → ''.
 */
export const relativeTage = (iso: string | null | undefined, jetzt: number): string => {
    const ms = zeitAus(iso);
    if (ms === null || !Number.isFinite(jetzt)) return '';
    const tage = berlinerTagNr(jetzt) - berlinerTagNr(ms);
    if (tage <= 0) return 'heute';
    if (tage === 1) return 'vor 1 Tag';
    return `vor ${tage} Tagen`;
};

/** „Mo 26.10., 09:14“ in Berliner Zeit; unlesbar → ''. */
export const formatZeitpunkt = (iso: string | null | undefined): string => {
    const ms = zeitAus(iso);
    if (ms === null) return '';
    const t = berlinTeile(ms);
    return `${t.wochentag} ${t.tag}.${t.monat}., ${t.stunde}:${t.minute}`;
};

/** „Mo 26.10.“ in Berliner Zeit; unlesbar → ''. */
export const formatTag = (iso: string | null | undefined): string => {
    const ms = zeitAus(iso);
    if (ms === null) return '';
    const t = berlinTeile(ms);
    return `${t.wochentag} ${t.tag}.${t.monat}.`;
};

/**
 * Läuft die Sperre noch? Unlesbares `gesperrtBis` gilt als abgelaufen: dann
 * steht der Knopf da, und `material_melden` bleibt der Riegel (antwortet mit
 * `gesperrt`, die Seite zeigt den Stand).
 */
export const istGesperrt = (meldung: MaterialMeldungKurz | null | undefined, jetzt: number): boolean => {
    if (!meldung) return false;
    const bis = zeitAus(meldung.gesperrtBis);
    return bis !== null && bis > jetzt;
};

// ─── Namen ───────────────────────────────────────────────────────────────────

/** Länge aus dem CHECK `melder_name` (Nr. 178). */
export const MAX_NAME = 80;

/** localStorage: der freiwillige Name, gemerkt für die nächste Karte. */
export const NAME_SPEICHER_SCHLUESSEL = 'osia.material.melderName';

/** Getrimmt, Leerraum zusammengezogen, höchstens 80 Zeichen; nie null. */
export const bereinigeName = (roh: unknown): string =>
    typeof roh === 'string' ? roh.replace(/\s+/g, ' ').trim().slice(0, MAX_NAME).trim() : '';

/** „Vor- Nachname“ aus dem eigenen Profil; nichts davon → null. */
export const nameAusProfil = (vorname: unknown, nachname: unknown): string | null =>
    bereinigeName([vorname, nachname].filter((x) => typeof x === 'string').join(' ')) || null;

// ─── Texte ───────────────────────────────────────────────────────────────────

export const TEXTE = {
    seitenTitel: 'Nachbestellen',
    laedt: 'Karte wird geladen …',
    ungueltigTitel: 'Karte ungültig',
    ungueltig: 'Diese Karte ist nicht mehr gültig. Bitte im Büro Bescheid geben.',
    lagerort: 'Lagerort',
    anleitung: 'Bestand erreicht? Auf „Bestätigen“ tippen und danach die Karte umdrehen.',
    knopf: 'Bestätigen',
    knopfUnterzeile: 'Bestand niedrig – nachbestellen',
    knopfUnterzeileErneut: 'Noch nicht aufgefüllt – erneut melden',
    knopfSendet: 'Wird gemeldet …',
    meldungAls: (name: string) => `Meldung als ${name}`,
    meldungAngemeldet: 'Meldung mit deiner Anmeldung',
    nameLabel: 'Dein Name (freiwillig)',
    namePlatzhalter: 'z. B. Max Mustermann',
    dankeTitel: 'Danke, ist gemeldet.',
    umdrehen: 'Bitte die Karte jetzt umdrehen.',
    schliessen: 'Du kannst die Seite jetzt schließen.',
    erinnertTitel: 'Erneut gemeldet, die Beschaffung wurde erinnert.',
    umgedrehtLassen: 'Karte bitte umgedreht lassen.',
    schonAusgeloest: 'Bestandsmeldung wurde schon ausgelöst.',
    statusBestellt: 'Status: bestellt',
    statusBestelltAm: (tag: string) => `Status: bestellt am ${tag}`,
    statusOffen: 'Status: gemeldet, noch nicht bestellt',
    zuVieleTitel: 'Gerade zu viele Meldungen',
    zuViele: 'Es gehen gerade zu viele Meldungen ein. Bitte in ein paar Minuten erneut versuchen.',
    fehlerTitel: 'Das hat nicht geklappt',
    fehlerAllgemein: 'Die Meldung konnte nicht gesendet werden. Bitte erneut versuchen.',
    ladeFehler: (detail: string) =>
        `Die Karte konnte nicht geladen werden${detail ? ` (${detail})` : ''}. Bitte Netz prüfen und erneut versuchen.`,
    erneutVersuchen: 'Erneut versuchen',
} as const;

/** Satzende anhängen, ohne „Max M..“ zu erzeugen. */
const satz = (s: string): string => (/[.!?]$/.test(s) ? s : `${s}.`);

/**
 * Was ein zweiter Scan während der Sperre zeigt:
 * „Bestandsmeldung wurde schon ausgelöst – vor 1 Tag (Mo 26.10., 09:14) von Max M.“
 *
 * Bezug ist die LETZTE Auslösung (sie hat die Sperre gesetzt). Der Melder
 * steht nur dabei, solange es eine Auslösung gab — `melder_kurz` gehört zur
 * ersten Meldung, eine Erinnerung ändert ihn nicht. Bei mehreren Auslösungen
 * folgt deshalb eine zweite Zeile mit der ersten Meldung.
 */
export const ausgeloestZeilen = (meldung: MaterialMeldungKurz | null | undefined, jetzt: number): string[] => {
    if (!meldung) return [TEXTE.schonAusgeloest];
    const mehrfach = meldung.anzahl > 1;
    const rel = relativeTage(meldung.zuletztAusgeloestAm, jetzt);
    const zp = formatZeitpunkt(meldung.zuletztAusgeloestAm);
    const wann = [mehrfach && rel ? `zuletzt ${rel}` : rel, zp ? `(${zp})` : ''].filter(Boolean).join(' ');
    const von = !mehrfach && meldung.melderKurz ? ` von ${meldung.melderKurz}` : '';
    const zeilen = [satz(`Bestandsmeldung wurde schon ausgelöst${wann ? ` – ${wann}` : ''}${von}`)];
    if (mehrfach) {
        const erste = formatZeitpunkt(meldung.gemeldetAm);
        const melder = meldung.melderKurz ? ` von ${meldung.melderKurz}` : '';
        zeilen.push(`Erste Meldung${erste ? ` ${erste}` : ''}${melder}, insgesamt ${meldung.anzahl}× ausgelöst.`);
    }
    return zeilen;
};

/** „Status: bestellt am Di 27.10.“ bzw. „Status: gemeldet, noch nicht bestellt“. */
export const statusZeile = (meldung: MaterialMeldungKurz): string => {
    if (meldung.status === 'bestellt') {
        const tag = formatTag(meldung.bestelltAm);
        return tag ? TEXTE.statusBestelltAm(tag) : TEXTE.statusBestellt;
    }
    return TEXTE.statusOffen;
};

/** Sperre abgelaufen, Meldung noch offen: „Gemeldet vor 9 Tagen, noch nicht aufgefüllt – erneut melden?“ */
export const erinnerungFrage = (meldung: MaterialMeldungKurz, jetzt: number): string => {
    const rel = relativeTage(meldung.gemeldetAm, jetzt);
    return `Gemeldet${rel ? ` ${rel}` : ''}, noch nicht aufgefüllt – erneut melden?`;
};

/** Kopfzeile über dem Knopf bei vorhandener Sitzung. */
export const melderAnzeige = (profilName: string | null | undefined): string =>
    profilName ? TEXTE.meldungAls(profilName) : TEXTE.meldungAngemeldet;

// ─── Zustände ────────────────────────────────────────────────────────────────

export type MeldenSchritt =
    | 'laedt'
    | 'ungueltig'
    | 'bereit'
    | 'sendet'
    | 'danke'
    | 'erinnert'
    | 'schon_ausgeloest'
    | 'zu_viele'
    | 'fehler';

/**
 * Nach dem Laden der Karte: null → ungültig; offene Meldung mit laufender
 * Sperre → „schon ausgelöst“ (kein Knopf); sonst bereit — auch bei offener
 * Meldung mit abgelaufener Sperre, dann als Erinnerung.
 */
export const zustandNachLaden = (karte: MaterialKarte | null, jetzt: number): MeldenSchritt => {
    if (!karte) return 'ungueltig';
    return istGesperrt(karte.meldung, jetzt) ? 'schon_ausgeloest' : 'bereit';
};

export interface ZustandNachSenden {
    schritt: MeldenSchritt;
    /** Stand nach der Meldung; null = den bisherigen behalten. */
    karte: MaterialKarte | null;
    fehlerText: string | null;
}

/**
 * Antwort von `/api/material-melden` → nächster Schritt. `gesperrt` heißt: ein
 * anderer Scan war schneller (Rennen) — die Seite zeigt dasselbe wie beim
 * Laden mit laufender Sperre, mit dem Stand aus der Antwort.
 */
export const zustandNachSenden = (antwort: MeldenAntwort): ZustandNachSenden => {
    // `=== true`/`=== false` statt Wahrheitswert: ohne `strict` engt nur der
    // Vergleich mit dem Literal die Union ein.
    if (antwort.ok === true) {
        const schritt: MeldenSchritt =
            antwort.art === 'neu' ? 'danke' : antwort.art === 'erinnerung' ? 'erinnert' : 'schon_ausgeloest';
        return { schritt, karte: antwort.karte, fehlerText: null };
    }
    switch (antwort.fehler) {
        case 'token':
        case 'ungueltig':
            return { schritt: 'ungueltig', karte: null, fehlerText: null };
        case 'zu_viele':
            return { schritt: 'zu_viele', karte: null, fehlerText: null };
        default:
            return { schritt: 'fehler', karte: null, fehlerText: antwort.text?.trim() || TEXTE.fehlerAllgemein };
    }
};

// ─── Ansicht ─────────────────────────────────────────────────────────────────

export interface MeldenAnsicht {
    ton: 'neutral' | 'erfolg' | 'warnung' | 'fehler';
    titel: string | null;
    zeilen: string[];
    /** Groß hervorgehoben: „Bitte die Karte jetzt umdrehen.“ / „Karte bitte umgedreht lassen.“ */
    aufforderung: string | null;
    hinweis: string | null;
    /** Großer Knopf — null heißt: kein Knopf (z. B. Sperre läuft). */
    knopf: { text: string; unterzeile: string; sendet: boolean } | null;
    /** Namensfeld bzw. „Meldung als …“ zeigen. */
    melder: boolean;
    erneutVersuchen: boolean;
    /** Artikelkopf (Firma, Name, Lagerort, Hinweis) zeigen. */
    kopf: boolean;
}

export interface MeldenZustand {
    schritt: MeldenSchritt;
    karte: MaterialKarte | null;
    /** Zeitpunkt des letzten Stands (ms) — Bezug für „vor X Tagen“. */
    jetzt: number;
    fehlerText: string | null;
}

const LEER: MeldenAnsicht = {
    ton: 'neutral',
    titel: null,
    zeilen: [],
    aufforderung: null,
    hinweis: null,
    knopf: null,
    melder: false,
    erneutVersuchen: false,
    kopf: false,
};

/** Der ganze Bildschirm je Zustand — die Komponente bildet nur noch ab. */
export const ansichtFuer = (z: MeldenZustand): MeldenAnsicht => {
    const meldung = z.karte?.meldung ?? null;
    const kopf = !!z.karte;
    switch (z.schritt) {
        case 'laedt':
            return { ...LEER, zeilen: [TEXTE.laedt] };
        case 'ungueltig':
            return { ...LEER, ton: 'warnung', titel: TEXTE.ungueltigTitel, zeilen: [TEXTE.ungueltig] };
        case 'bereit':
        case 'sendet': {
            // Mit offener Meldung (Sperre abgelaufen) wird der Knopf zur Erinnerung.
            const sendet = z.schritt === 'sendet';
            return {
                ...LEER,
                ton: meldung ? 'warnung' : 'neutral',
                zeilen: meldung ? [erinnerungFrage(meldung, z.jetzt), statusZeile(meldung)] : [TEXTE.anleitung],
                knopf: {
                    text: sendet ? TEXTE.knopfSendet : TEXTE.knopf,
                    unterzeile: meldung ? TEXTE.knopfUnterzeileErneut : TEXTE.knopfUnterzeile,
                    sendet,
                },
                melder: true,
                kopf,
            };
        }
        case 'danke':
            return {
                ...LEER,
                ton: 'erfolg',
                titel: TEXTE.dankeTitel,
                aufforderung: TEXTE.umdrehen,
                hinweis: TEXTE.schliessen,
                kopf,
            };
        case 'erinnert':
            return {
                ...LEER,
                ton: 'erfolg',
                titel: TEXTE.erinnertTitel,
                zeilen: meldung ? [statusZeile(meldung)] : [],
                aufforderung: TEXTE.umdrehen,
                hinweis: TEXTE.schliessen,
                kopf,
            };
        case 'schon_ausgeloest':
            return {
                ...LEER,
                ton: 'warnung',
                zeilen: [...ausgeloestZeilen(meldung, z.jetzt), ...(meldung ? [statusZeile(meldung)] : [])],
                aufforderung: TEXTE.umgedrehtLassen,
                kopf,
            };
        case 'zu_viele':
            return {
                ...LEER,
                ton: 'warnung',
                titel: TEXTE.zuVieleTitel,
                zeilen: [TEXTE.zuViele],
                erneutVersuchen: true,
                kopf,
            };
        case 'fehler':
        default:
            return {
                ...LEER,
                ton: 'fehler',
                titel: TEXTE.fehlerTitel,
                zeilen: [z.fehlerText?.trim() || TEXTE.fehlerAllgemein],
                erneutVersuchen: true,
                kopf,
            };
    }
};
